import { create } from 'zustand';

/**
 * Quad-view navigation state (R5, Rhino-style quad layout).
 *
 * Each orthographic preview cell (Top/Front/Right) owns its camera framing —
 * zoom in pixels-per-world-unit and an orbit target in world space — exactly
 * like Rhino's independent viewports. The store lives outside the component
 * tree so the framing survives workbench switches (the cells unmount when
 * the 3D workbench is left and remount on return with their pose intact).
 *
 * `autoFrame` mirrors Rhino's first-view behavior: a cell keeps re-framing
 * to the document extent while it has never been touched by the user; the
 * first wheel zoom or middle-drag pan hands control over permanently.
 */
export type QuadDirection = 'top' | 'front' | 'right' | 'bottom' | 'back' | 'left';

/** Viewport ids of the 3D quad shell: the ortho cells plus the main
    perspective viewport ('main'). */
export type QuadViewportId = 'main' | QuadDirection;

export interface QuadCellView {
    /** Orthographic zoom in pixels per world unit. */
    zoom: number;
    /** Look-at point in world space. */
    target: [number, number, number];
    /** While true the cell keeps fitting itself to the document bounds. */
    autoFrame: boolean;
}

const INITIAL_CELL: QuadCellView = { zoom: 20, target: [0, 0, 0], autoFrame: true };

/** Split positions of the 2x2 quad layout as fractions of the quad area
    (0..1), shared by the splitters and the center resize handle. */
export interface QuadLayout {
    /** Horizontal split: width fraction of the left column. */
    columnRatio: number;
    /** Vertical split: height fraction of the top row. */
    rowRatio: number;
}

const INITIAL_LAYOUT: QuadLayout = { columnRatio: 0.5, rowRatio: 0.5 };

interface QuadViewStore {
    cells: Record<QuadDirection, QuadCellView>;
    setCell: (direction: QuadDirection, patch: Partial<QuadCellView>) => void;
    /** Force one re-frame on the given cells (ZoomExtents chords). */
    requestReframe: (directions?: QuadDirection[]) => void;
    /** 2x2 split positions (resizable from the center cross handle). */
    layout: QuadLayout;
    setLayout: (patch: Partial<QuadLayout>) => void;
    /** Viewport currently maximized over the quad, or null for the 2x2
        layout (Alt+W toggle, see QuadLayout). */
    maximized: QuadViewportId | null;
    setMaximized: (id: QuadViewportId | null) => void;
}

const ALL_DIRECTIONS: QuadDirection[] = ['top', 'front', 'right', 'bottom', 'back', 'left'];

export const useQuadViewStore = create<QuadViewStore>((set) => ({
    cells: {
        top: { ...INITIAL_CELL },
        front: { ...INITIAL_CELL },
        right: { ...INITIAL_CELL },
        bottom: { ...INITIAL_CELL },
        back: { ...INITIAL_CELL },
        left: { ...INITIAL_CELL },
    },
    setCell: (direction, patch) =>
        set((state) => ({
            cells: { ...state.cells, [direction]: { ...state.cells[direction], ...patch } },
        })),
    requestReframe: (directions) =>
        set((state) => {
            const targets = directions ?? ALL_DIRECTIONS;
            const cells = { ...state.cells };
            targets.forEach((direction) => {
                cells[direction] = { ...cells[direction], autoFrame: true };
            });
            return { cells };
        }),
    layout: { ...INITIAL_LAYOUT },
    setLayout: (patch) =>
        set((state) => ({ layout: { ...state.layout, ...patch } })),
    maximized: null,
    setMaximized: (maximized) => set({ maximized }),
}));
